/**
 * Card Media Swiper — transform-based slideshow for product cards.
 * Uses document-level capture-phase touch/pointer listeners to bypass any
 * ancestor (Dawn full-card link, card overlays, etc.) that might swallow events.
 */
class CardMediaSwiper extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.card-media-swiper__track');
    this.slides = this.querySelectorAll('.card-media-swiper__slide');
    this.dots = this.querySelectorAll('.card-media-swiper__dot');
    this.prevBtn = this.querySelector('.card-media-swiper__btn--prev');
    this.nextBtn = this.querySelector('.card-media-swiper__btn--next');
    this.count = this.slides.length;
    this.currentIndex = 0;

    const cardWrapper = this.closest('.card-wrapper');
    const cardLink = cardWrapper?.querySelector('.card__heading a, a.full-unstyled-link');
    this.productUrl = cardLink?.getAttribute('href') || null;

    if (!this.track || this.count <= 1) return;

    this.goTo(0, false);
    this.setupButtons();
    this.setupDots();
    this.setupKeyboard();
    this.updateButtons();

    window.addEventListener('resize', () => this.goTo(this.currentIndex, false));
  }

  setupButtons() {
    this.prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.goTo(this.currentIndex - 1, true);
    });
    this.nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.goTo(this.currentIndex + 1, true);
    });
  }

  setupDots() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goTo(index, true);
      });
    });
  }

  setupKeyboard() {
    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.goTo(this.currentIndex + 1, true);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.goTo(this.currentIndex - 1, true);
      }
    });
  }

  dragTo(dx) {
    const width = this.track.clientWidth || 1;
    const percent = -this.currentIndex * 100 + (dx / width) * 100;
    this.track.style.transform = `translateX(${percent}%)`;
  }

  settleDrag(dx, elapsed) {
    this.track.classList.remove('is-dragging');
    const width = this.track.clientWidth || 1;
    const velocity = Math.abs(dx) / Math.max(elapsed, 1);
    const threshold = velocity > 0.4 ? 20 : width * 0.15;
    let target = this.currentIndex;
    if (dx < -threshold) target = Math.min(this.currentIndex + 1, this.count - 1);
    else if (dx > threshold) target = Math.max(this.currentIndex - 1, 0);
    this.goTo(target, true);
  }

  // Slides after the first ship without src/srcset (card-product.liquid) so a
  // grid only downloads one image per card. Load them once the shopper
  // shows interest in this card.
  hydrate() {
    if (this.hydrated) return;
    this.hydrated = true;
    this.querySelectorAll('img[data-src]').forEach((img) => {
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
    });
  }

  goTo(index, animate) {
    if (index !== 0) this.hydrate();
    const clamped = Math.max(0, Math.min(index, this.count - 1));
    this.currentIndex = clamped;

    if (!animate) this.track.classList.add('is-dragging');
    this.track.style.transform = `translateX(${-clamped * 100}%)`;
    if (!animate) requestAnimationFrame(() => this.track.classList.remove('is-dragging'));

    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === clamped));
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === clamped));
    this.updateButtons();
  }

  updateButtons() {
    this.prevBtn?.classList.toggle('is-hidden', this.currentIndex === 0);
    this.nextBtn?.classList.toggle('is-hidden', this.currentIndex === this.count - 1);
  }
}

if (!customElements.get('card-media-swiper')) {
  customElements.define('card-media-swiper', CardMediaSwiper);
}

// ---------------------------------------------------------------
// Document-level capture-phase touch handling. This bypasses any
// ancestor stopPropagation or link hijacking — events fire on the
// document before reaching card elements.
// ---------------------------------------------------------------
(function () {
  let active = null; // { swiper, startX, startY, startT, axis, delta, didSwipe }

  const findSwiper = (target) => {
    if (!target || !target.closest) return null;
    if (target.closest('.card-media-swiper__btn, .card-media-swiper__dot')) return null;
    return target.closest('card-media-swiper');
  };

  const swiperAtPoint = (x, y) => {
    const stack = document.elementsFromPoint ? document.elementsFromPoint(x, y) : [];
    for (const el of stack) {
      // Skip if the click is on a chevron, dot, or the quick-add button — those have their own handlers
      if (el.closest?.('.card-media-swiper__btn, .card-media-swiper__dot, .card__image-quick-add')) return null;
      const s = el.closest?.('card-media-swiper');
      if (s) return s;
    }
    return null;
  };

  const startOnSwiper = (swiper, x, y) => {
    if (!swiper || !swiper.track || swiper.count <= 1) return;
    swiper.hydrate();
    active = {
      swiper,
      startX: x,
      startY: y,
      startT: Date.now(),
      axis: null,
      delta: 0,
      didSwipe: false,
    };
    swiper.track.classList.add('is-dragging');
  };

  const start = (x, y, target) => {
    const swiper = findSwiper(target) || swiperAtPoint(x, y);
    if (!swiper) return;
    startOnSwiper(swiper, x, y);
  };

  const move = (x, y, e) => {
    if (!active) return;
    const dx = x - active.startX;
    const dy = y - active.startY;

    if (!active.axis) {
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
        active.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
    }

    if (active.axis === 'x') {
      if (e && e.cancelable) e.preventDefault();
      active.delta = dx;
      active.didSwipe = true;
      active.swiper.dragTo(dx);
    }
  };

  const end = () => {
    if (!active) return;
    const { swiper, axis, delta, didSwipe, startT } = active;
    active = null;

    if (axis === 'x') {
      swiper.settleDrag(delta, Date.now() - startT);
      if (didSwipe) {
        // Swallow next click so swipe doesn't navigate to PDP
        const swallow = (ev) => {
          if (!ev.target.closest('card-media-swiper')) return;
          ev.preventDefault();
          ev.stopPropagation();
          ev.stopImmediatePropagation();
        };
        document.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => document.removeEventListener('click', swallow, true), 400);
      }
    } else {
      swiper.track.classList.remove('is-dragging');
    }
  };

  // Touch
  document.addEventListener('touchstart', (e) => {
    const t = e.touches[0];
    const swiper = swiperAtPoint(t.clientX, t.clientY);
    if (!swiper) return;
    // Pass the swiper directly — bypass target/closest hit test
    startOnSwiper(swiper, t.clientX, t.clientY);
  }, { capture: true, passive: true });

  document.addEventListener('touchmove', (e) => {
    if (!active) return;
    const t = e.touches[0];
    move(t.clientX, t.clientY, e);
  }, { capture: true, passive: false });

  document.addEventListener('touchend', end, { capture: true });
  document.addEventListener('touchcancel', end, { capture: true });

  // Mouse (desktop drag)
  document.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    start(e.clientX, e.clientY, e.target);
    if (!active) return;
    const mm = (ev) => move(ev.clientX, ev.clientY, ev);
    const mu = () => {
      document.removeEventListener('mousemove', mm);
      document.removeEventListener('mouseup', mu);
      end();
    };
    document.addEventListener('mousemove', mm);
    document.addEventListener('mouseup', mu);
  }, { capture: true });

  // Tap (no drag) → PDP
  document.addEventListener('click', (e) => {
    const swiper = findSwiper(e.target);
    if (!swiper || !swiper.productUrl) return;
    // If settleDrag cleaned up already, no active; otherwise ignore
    window.location.href = swiper.productUrl;
  });
})();
